import React, { useContext, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import "./Serviceitem.css";
import progressContext from "../../context/loading/progresscontext";
import servicesContext from "../../context/services/servicescontext";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import CtaBand from "../home/ctaband/CtaBand";

// Predefined rich content for all core medical departments
const departmentsData = {
  gynaecology: {
    slug: "gynaecology",
    aliases: ["gynaecology", "gynaecology-maternity", "maternity", "womens-health"],
    eyebrow: "Obstetrics & Gynaecology Specialty",
    title: "Gynaecology & Maternity Care",
    lead: "Compassionate, evidence-based care for every stage of womanhood — from preconception and gentle childbirth to laparoscopic surgery and menopause support.",
    heroImage: "/img/uplaods/mother-loves-her-child.avif",
    doctorName: "Dr. Nishita Rao",
    doctorRole: "Consultant Obstetrician & Gynaecologist",
    doctorQuals: "MBBS, MS (Obstetrics & Gynaecology), FMAS (Minimal Access Surgery)",
    doctorExp: "18+ Years Experience",
    doctorImg: "/img/doctors/dr-nishita-rao.webp",
    keyStats: [
      { num: "90%", label: "Normal Delivery Success" },
      { num: "18+", label: "Years Clinical Experience" },
      { num: "24/7", label: "Obstetric Emergency Support" }
    ],
    overview:
      "Under the leadership of Dr. Nishita Rao, our Department of Gynaecology & Obstetrics provides holistic medical care with a patient-first ethos. We specialize in promoting natural, gentle childbirth while ensuring the highest clinical safety standards. From adolescent health consultations to advanced laparoscopic surgery, every patient receives personalized attention and unhurried consultations.",
    serviceCategories: [
      {
        title: "Antenatal & Maternity Care",
        items: [
          {
            name: "Preconception Counseling & Health Optimization",
            desc: "Nutritional advice, genetic screening, and baseline wellness checks before conception."
          },
          {
            name: "Antenatal Check-ups & High-Res Ultrasound",
            desc: "Regular clinical assessments, growth scans, and fetal well-being evaluations."
          },
          {
            name: "Normal Delivery with Gentle Labor Care",
            desc: "Over 90% normal delivery rate with continuous midwifery support and painless delivery (epidural) options."
          },
          {
            name: "High-Risk Pregnancy Management",
            desc: "Dedicated clinical protocols for gestational diabetes, pre-eclampsia, and multiple births."
          },
          {
            name: "Postnatal Care & Lactation Support",
            desc: "In-hospital postpartum monitoring, breastfeeding guidance, and new mother wellness."
          }
        ]
      },
      {
        title: "Gynaecology & Surgical Treatments",
        items: [
          {
            name: "Laparoscopic & Minimally Invasive Surgery",
            desc: "Keyhole procedures for ovarian cysts, uterine fibroids, and ectopic pregnancies with rapid recovery."
          },
          {
            name: "PCOS Power Protocol",
            desc: "Comprehensive lifestyle, dietary, and hormonal therapies to regulate menstrual cycles and fertility."
          },
          {
            name: "Cervical Cancer Screening & HPV Vaccination",
            desc: "Routine Pap smears, colposcopy, and Gardasil 9 vaccination for lifetime protection."
          },
          {
            name: "Infertility Diagnostics & Treatment",
            desc: "Ovulation tracking, follicular monitoring, and personalized reproductive counseling."
          },
          {
            name: "Menopause & Pelvic Health",
            desc: "Managing perimenopausal symptoms, bone health density monitoring, and hormonal balance."
          }
        ]
      }
    ],
    whyChoose: [
      "Natural-birth-first philosophy with high success in vaginal deliveries",
      "One-to-one compassionate care from admission through discharge",
      "Sterile, modern labor suites and immediate surgical backup if required",
      "Transparent pricing with no hidden maternity package costs"
    ]
  },

  pediatric: {
    slug: "pediatric",
    aliases: ["pediatric", "pediatrics", "paediatrics", "paediatrics-child-care", "child-care"],
    eyebrow: "Paediatrics & Neonatal Care",
    title: "Paediatrics & Child Care",
    lead: "Gentle, expert medical care for newborns, toddlers, and growing children in a calm, family-friendly environment with over 21 years of trusted clinical experience.",
    heroImage: "/img/uplaods/mother-child.jpg",
    doctorName: "Dr. Mahesh Rao",
    doctorRole: "Senior Consultant Paediatrician",
    doctorQuals: "MBBS, DCH (Diploma in Child Health)",
    doctorExp: "21+ Years Experience",
    doctorImg: "/img/doctors/dr-mahesh-rao.webp",
    keyStats: [
      { num: "21+", label: "Years Caring for Children" },
      { num: "100%", label: "WHO Vaccine Schedule Available" },
      { num: "Child", label: "Friendly, Gentle Environment" }
    ],
    overview:
      "Led by Dr. Mahesh Rao, our Paediatrics Department is dedicated to nurturing the health and developmental milestones of your child. We combine clinical thoroughness with a calm, reassuring manner that puts children and parents at ease during every visit. From newborn health checks to adolescent developmental guidance, your family is in experienced hands.",
    serviceCategories: [
      {
        title: "Infant & Early Years Care",
        items: [
          {
            name: "Newborn Health Checks & APGAR Screening",
            desc: "Comprehensive physical exams, neonatal jaundice monitoring, and developmental checks in the first weeks."
          },
          {
            name: "Complete Childhood Immunisation",
            desc: "All mandatory and recommended childhood vaccines administered with utmost care and gentle techniques."
          },
          {
            name: "Growth & Milestone Tracking",
            desc: "Monitoring physical growth, cognitive development, speech, and motor skills with nutritional guidance."
          },
          {
            name: "Infant Feeding & Weaning Advice",
            desc: "Guidance on breastfeeding, formula feeding, and introducing solid foods for optimal gut health."
          }
        ]
      },
      {
        title: "Illness Management & Growing Up",
        items: [
          {
            name: "Acute Childhood Infections & Fevers",
            desc: "Rapid diagnostic evaluation and treatment of respiratory, gastrointestinal, and viral illnesses."
          },
          {
            name: "Paediatric Asthma & Allergy Care",
            desc: "Allergy testing, nebulization therapy, and long-term asthma action plans for children."
          },
          {
            name: "Paediatric Dermatology",
            desc: "Gentle treatments for infantile eczema, skin allergies, cradle cap, and viral exanthems."
          },
          {
            name: "Adolescent Health & Wellness",
            desc: "Puberty-related guidance, emotional wellness support, and nutritional counseling for teenagers."
          }
        ]
      }
    ],
    whyChoose: [
      "Over 21 years of specialized paediatric experience in Malleswaram",
      "Minimal waiting times to avoid distress for sick children",
      "Comprehensive vaccine inventory stored under strict cold-chain protocols",
      "Unhurried consultations with detailed explanations for parents"
    ]
  },

  "general-services": {
    slug: "general-services",
    aliases: ["general-services", "general", "day-care-surgery", "surgical-care"],
    eyebrow: "Hospital Facilities & Clinical Care",
    title: "General Medical & Day Care Surgery",
    lead: "Sterile surgical facilities, 24/7 emergency response, and comprehensive primary care supporting families throughout Malleswaram and North Bangalore.",
    heroImage: "/img/uplaods/General-services.webp",
    doctorName: "Clinical & Surgical Care Team",
    doctorRole: "Multi-disciplinary Medical Staff",
    doctorQuals: "Experienced Physicians, Surgeons & Nursing Specialists",
    doctorExp: "24/7 In-House Support",
    doctorImg: "/img/doctors/dr-nishita-rao.webp",
    keyStats: [
      { num: "24/7", label: "Emergency & Labor Readiness" },
      { num: "Sterile", label: "Modern Operation Theatres" },
      { num: "In-House", label: "Diagnostics & Pharmacy" }
    ],
    overview:
      "Alongside our specialized maternity and paediatric services, Lakshmi Maternity & Surgical Centre offers a full suite of general healthcare, minor surgeries, and day-care clinical treatments. Our hospital is equipped with sterile operating suites, diagnostic laboratory services, clean private recovery rooms, and 24/7 emergency readiness.",
    serviceCategories: [
      {
        title: "Clinical Facilities & Emergency Care",
        items: [
          {
            name: "24/7 Emergency & Maternity Admissions",
            desc: "Round-the-clock medical readiness with on-duty clinical team and immediate specialist access."
          },
          {
            name: "Fully Equipped Operation Theatres",
            desc: "Modern sterile surgical suites with HEPA filtration for elective and emergency procedures."
          },
          {
            name: "Inpatient Recovery & Private Suites",
            desc: "Clean, hygienic private and semi-private rooms with dedicated nursing care and patient amenities."
          },
          {
            name: "In-House Diagnostic Laboratory",
            desc: "Rapid turnaround on blood panels, biochemistry, urinalysis, and prenatal diagnostic tests."
          }
        ]
      },
      {
        title: "Primary Healthcare & Day Care Surgery",
        items: [
          {
            name: "Day Care Surgical Procedures",
            desc: "Minor gynaecological, general surgical, and wound management procedures with same-day discharge."
          },
          {
            name: "Adult Preventive Health Check-ups",
            desc: "Comprehensive health screening packages including diabetes, thyroid, and cardiovascular screenings."
          },
          {
            name: "Pharmacy & Medication Dispensation",
            desc: "Well-stocked in-house pharmacy providing authentic prescription medicines and maternity essentials."
          },
          {
            name: "Post-Operative Recovery Care",
            desc: "Personalized post-surgical wound care, pain management, and follow-up clinical assessments."
          }
        ]
      }
    ],
    whyChoose: [
      "Centrally located on Margosa Road, Malleswaram with easy access",
      "Round-the-clock doctor and nursing staff availability",
      "Transparent hospital billing with ethical clinical practices",
      "Spotless hygiene standards and infection-controlled environments"
    ]
  }
};

export default function Serviceitem(props) {
  const { slug } = useParams();
  const location = useLocation();
  const { setProgress } = useContext(progressContext);
  const { services } = useContext(servicesContext);

  // Extract slug from useParams, props, or location.pathname
  let currentSlug = slug || (props.serviceItem && props.serviceItem.slug);
  if (!currentSlug) {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length >= 2 && parts[0] === "services") {
      currentSlug = parts[1];
    }
  }
  if (!currentSlug) {
    currentSlug = "gynaecology";
  }
  currentSlug = currentSlug.toLowerCase().trim();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (setProgress) {
      setProgress(70);
      setTimeout(() => {
        setProgress(100);
      }, 150);
    }
  }, [currentSlug, location.pathname, setProgress]);

  // Match predefined department data
  let dept = departmentsData[currentSlug];
  if (!dept) {
    // Search aliases
    for (const key in departmentsData) {
      if (departmentsData[key].aliases.includes(currentSlug)) {
        dept = departmentsData[key];
        break;
      }
    }
  }

  // If still not matched, check dynamic context or fallback to gynaecology
  if (!dept) {
    const dynamicMatch = services && services.find((s) => s.slug === currentSlug);
    if (dynamicMatch) {
      dept = {
        slug: dynamicMatch.slug,
        eyebrow: "Medical Department",
        title: dynamicMatch.title,
        lead: dynamicMatch.description ? dynamicMatch.description.replace(/<[^>]+>/g, '') : "Specialized healthcare services at Lakshmi Maternity & Surgical Centre.",
        heroImage: dynamicMatch.image ? `/img/uplaods/${dynamicMatch.image}` : "/img/uplaods/mother-loves-her-child.avif",
        doctorName: "Dr. Nishita Rao & Dr. Mahesh Rao",
        doctorRole: "Senior Medical Consultants",
        doctorQuals: "Lakshmi Maternity & Surgical Centre",
        doctorExp: "18+ & 21+ Years Experience",
        doctorImg: "/img/doctors/dr-nishita-rao.webp",
        keyStats: [
          { num: "90%", label: "Patient Satisfaction" },
          { num: "2011", label: "Established in Malleswaram" },
          { num: "24/7", label: "Medical Readiness" }
        ],
        overview: dynamicMatch.description2 ? dynamicMatch.description2.replace(/<[^>]+>/g, '') : dynamicMatch.description,
        serviceCategories: [
          {
            title: "Specialized Clinical Services",
            items: [
              {
                name: dynamicMatch.title,
                desc: dynamicMatch.description ? dynamicMatch.description.replace(/<[^>]+>/g, '') : "Comprehensive medical examination and specialized therapy."
              }
            ]
          }
        ],
        whyChoose: [
          "Experienced medical specialists with decades of proven practice",
          "Clean, modern clinical environment in the heart of Malleswaram",
          "Personalized care tailored to your individual health requirements"
        ]
      };
    } else {
      dept = departmentsData.gynaecology;
    }
  }

  return (
    <main className="service-detail-page">
      <Breadcrumb
        pageName={dept.title}
        bgImage="/img/uplaods/rad.jpg"
        path={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/#care" },
          { label: dept.title }
        ]}
      />

      {/* Hero Overview Band */}
      <section className="service-hero-band">
        <div className="wrap">
          <div className="service-hero-grid">
            <div className="service-hero-copy">
              <span className="dept-badge">{dept.eyebrow}</span>
              <h1 className="service-hero-title">{dept.title}</h1>
              <p className="service-hero-lead">{dept.lead}</p>

              <div className="service-hero-stats">
                {dept.keyStats.map((st, idx) => (
                  <div key={idx} className="service-stat-item">
                    <strong className="service-stat-num">{st.num}</strong>
                    <span className="service-stat-lbl">{st.label}</span>
                  </div>
                ))}
              </div>

              <div className="service-hero-actions">
                <Link to="/appointment" className="btn-solid">
                  Book an Appointment
                </Link>
                <a href="tel:08023347276" className="btn-outline">
                  Call 080 2334 7276
                </a>
              </div>
            </div>

            <div className="service-hero-media">
              <figure className="service-hero-figure">
                <img
                  src={dept.heroImage}
                  alt={dept.title}
                  className="service-hero-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/img/uplaods/mother-loves-her-child.avif";
                  }}
                />
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Department Navigation Switcher */}
      <section className="service-nav-bar">
        <div className="wrap">
          <div className="service-nav-pills">
            <span className="service-nav-label">Our Departments:</span>
            <Link
              to="/services/gynaecology"
              className={`service-nav-pill ${dept.slug === "gynaecology" ? "active" : ""}`}
            >
              Gynaecology &amp; Maternity
            </Link>
            <Link
              to="/services/pediatric"
              className={`service-nav-pill ${dept.slug === "pediatric" ? "active" : ""}`}
            >
              Paediatrics &amp; Child Care
            </Link>
            <Link
              to="/services/general-services"
              className={`service-nav-pill ${dept.slug === "general-services" ? "active" : ""}`}
            >
              General Services
            </Link>
          </div>
        </div>
      </section>

      {/* Main Clinical Details & Doctor Profile */}
      <section className="band">
        <div className="wrap">
          <div className="service-content-split">
            {/* Left Main Content */}
            <div className="service-main-col">
              <div className="service-overview-block">
                <h2 className="service-section-heading">Clinical Overview</h2>
                <p className="service-overview-text">{dept.overview}</p>
              </div>

              {/* Service Categories Grid */}
              <div className="service-categories-stack">
                {dept.serviceCategories.map((cat, catIdx) => (
                  <div key={catIdx} className="service-category-card">
                    <h3 className="service-category-title">{cat.title}</h3>
                    <ul className="service-items-list">
                      {cat.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="service-item-row">
                          <div className="service-item-marker">✓</div>
                          <div>
                            <h4 className="service-item-name">{item.name}</h4>
                            <p className="service-item-desc">{item.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Why Choose Block */}
              <div className="service-why-card">
                <h3 className="service-why-title">Why Choose Us for {dept.title}</h3>
                <ul className="service-why-list">
                  {dept.whyChoose.map((point, pIdx) => (
                    <li key={pIdx} className="service-why-item">
                      <span className="why-bullet">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Sidebar: Lead Doctor & Quick Info */}
            <aside className="service-sidebar">
              {/* Doctor Card */}
              <div className="service-doctor-card">
                <div className="sidebar-doc-header">
                  <div className="sidebar-doc-avatar">
                    <img
                      src={dept.doctorImg}
                      alt={dept.doctorName}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/img/doctors/dr-nishita-rao.webp";
                      }}
                    />
                  </div>
                  <div>
                    <span className="sidebar-doc-tag">Department Lead</span>
                    <h3 className="sidebar-doc-name">{dept.doctorName}</h3>
                    <p className="sidebar-doc-role">{dept.doctorRole}</p>
                  </div>
                </div>

                <div className="sidebar-doc-body">
                  <p className="sidebar-doc-quals">
                    <strong>Qualifications:</strong> {dept.doctorQuals}
                  </p>
                  <p className="sidebar-doc-exp">
                    <strong>Experience:</strong> {dept.doctorExp}
                  </p>
                  <Link to="/doctors" className="sidebar-doc-link">
                    View Full Profile &rarr;
                  </Link>
                </div>

                <div className="sidebar-doc-action">
                  <Link to="/appointment" className="btn-solid btn-block">
                    Book Consultation
                  </Link>
                </div>
              </div>

              {/* Quick Contact & Timings Widget */}
              <div className="service-widget-card">
                <h4 className="widget-title">Hospital Timings &amp; Location</h4>
                <ul className="widget-info-list">
                  <li>
                    <strong>OPD Hours:</strong>
                    <span>Mon – Sat: 9:00 AM – 8:00 PM</span>
                  </li>
                  <li>
                    <strong>Emergency &amp; Labor:</strong>
                    <span>Open 24 Hours / 7 Days</span>
                  </li>
                  <li>
                    <strong>Address:</strong>
                    <span>95 &amp; 96, Margosa Road, Opposite Malleswaram Exchange, Bangalore</span>
                  </li>
                  <li>
                    <strong>Direct Phone:</strong>
                    <a href="tel:08023347276">080 2334 7276</a>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
